/**
 * @fileoverview Membership & Share Capital — Share Capital Segmentation page.
 * @description Ported from the InsightBox Banking design system's own
 * `MembershipShareCapital/ShareCapitalSegmentation.tsx`. The reference hardcodes every KPI's
 * "Highest in 5 Years" sublabel without actually checking — replaced here with a real
 * `isHighestIn5Y` check against the same `core(...).totalMembers`/`.shareCapital` series the bands
 * are reconciled to, per the Banking module plan's "clean up demo-fragile inline calculations,
 * don't reproduce verbatim" note.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  BranchFilterSelect,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  branchLabel,
  core,
  formatCr,
  formatNumber,
  formatRupee,
  isHighestIn5Y,
  resolveBankingAccessScope,
  resolveFy,
  shareCapitalBands,
  useBankingData,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'
import { PieBreakdownCardGroup } from '@/shared/components/molecules/PieBreakdownCard'
import { categoricalPalette } from '@/shared/constants/chart-theme.constants'

import type { FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'
import type { PieGroup } from '@/shared/components/molecules/PieBreakdownCard'

/**
 * @component
 */
function BankingMembershipShareCapitalSegmentationPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.membershipShareCapitalSegmentation.kicker')}
      title={t('banking.membershipShareCapitalSegmentation.title')}
      description={t('banking.membershipShareCapitalSegmentation.description')}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const fy = resolveFy(fyFilter)
  const bands = shareCapitalBands(data, fy, branchId)

  const totalMembers = bands.reduce((sum, b) => sum + b.members, 0)
  const totalCapital = bands.reduce((sum, b) => sum + b.capitalCr, 0)
  const avgPerMember = (totalCapital * 1e7) / Math.max(totalMembers, 1)
  const topBand = [...bands].sort((a, b) => b.capitalCr - a.capitalCr)[0]!
  const concentration = (topBand.capitalCr / totalCapital) * 100
  const highestMembers = isHighestIn5Y(fy, (f) => core(data, f, branchId).totalMembers)
  const highestCapital = isHighestIn5Y(fy, (f) => core(data, f, branchId).shareCapital)

  const colors = categoricalPalette(bands.length)

  const groups: PieGroup[] = [
    {
      key: 'members',
      label: t('banking.membershipShareCapitalSegmentation.groups.members.label'),
      sublabel: t('banking.membershipShareCapitalSegmentation.groups.members.sublabel'),
      categoryColumnLabel: t('banking.membershipShareCapitalSegmentation.groups.bandColumn'),
      valueColumnLabel: t('banking.membershipShareCapitalSegmentation.groups.members.valueColumn'),
      segments: bands.map((b, i) => ({
        key: b.key,
        label: b.label,
        value: b.members,
        pct: (b.members / totalMembers) * 100,
        color: colors[i]!,
      })),
    },
    {
      key: 'capital',
      label: t('banking.membershipShareCapitalSegmentation.groups.capital.label'),
      sublabel: t('banking.membershipShareCapitalSegmentation.groups.capital.sublabel'),
      categoryColumnLabel: t('banking.membershipShareCapitalSegmentation.groups.bandColumn'),
      valueColumnLabel: t('banking.membershipShareCapitalSegmentation.groups.capital.valueColumn'),
      segments: bands.map((b, i) => ({
        key: b.key,
        label: b.label,
        value: b.capitalCr,
        pct: (b.capitalCr / totalCapital) * 100,
        color: colors[i]!,
      })),
    },
  ]

  const kpis: BankingKpiStripItem[] = [
    {
      label: t('banking.membershipShareCapitalSegmentation.kpi.totalMembers'),
      value: formatNumber(totalMembers),
      sublabel: highestMembers
        ? t('banking.membershipShareCapitalSegmentation.kpi.highestIn5Y')
        : fy,
      highlight: true,
      ...(highestMembers && { trend: { direction: 'up' } }),
    },
    {
      label: t('banking.membershipShareCapitalSegmentation.kpi.totalShareCapital'),
      value: formatCr(totalCapital),
      sublabel: highestCapital
        ? t('banking.membershipShareCapitalSegmentation.kpi.highestIn5Y')
        : fy,
      ...(highestCapital && { trend: { direction: 'up' } }),
    },
    {
      label: t('banking.membershipShareCapitalSegmentation.kpi.avgPerMember'),
      value: formatRupee(avgPerMember),
      sublabel: highestCapital
        ? t('banking.membershipShareCapitalSegmentation.kpi.highestIn5Y')
        : fy,
      ...(highestCapital && { trend: { direction: 'up' } }),
    },
    {
      label: t('banking.membershipShareCapitalSegmentation.kpi.capitalConcentration'),
      value: `${concentration.toFixed(2)}%`,
      sublabel: t('banking.membershipShareCapitalSegmentation.kpi.concentrationSublabel', {
        band: topBand.label,
      }),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        title={t('banking.membershipShareCapitalSegmentation.chartTitle')}
        subtitle={t('banking.membershipShareCapitalSegmentation.chartSubtitle', {
          branch: branchLabel(branchId),
        })}
      >
        <PieBreakdownCardGroup
          groups={groups}
          valueFormatter={(v, groupKey) => (groupKey === 'members' ? formatNumber(v) : formatCr(v))}
          totalLabel={t('banking.total')}
        />
      </ChartCard>

      <BusinessSummaryPanel
        variant="bare"
        title={t('banking.membershipShareCapitalSegmentation.businessSummaryTitle', { fy })}
        lines={[
          t('banking.membershipShareCapitalSegmentation.summary.line1', {
            memberSharePct: ((bands[0]!.members / totalMembers) * 100).toFixed(0),
            band: bands[0]!.label,
            capitalSharePct: ((bands[0]!.capitalCr / totalCapital) * 100).toFixed(1),
          }),
          t('banking.membershipShareCapitalSegmentation.summary.line2', {
            band: topBand.label,
            memberSharePct: ((topBand.members / totalMembers) * 100).toFixed(1),
            capitalSharePct: concentration.toFixed(1),
          }),
        ]}
      />
    </div>
  )
}

export default BankingMembershipShareCapitalSegmentationPage
